import { type AbstractControl, type ValidationErrors } from "@angular/forms";

interface FormatOptions {
  dots?: boolean;
}

export class RutUtils {
  static clean(rut: string | null | undefined) {
    return typeof rut === "string"
      ? rut.replace(/^0+|[^0-9kK]+/g, "").toUpperCase()
      : "";
  }

  static validate(rut: string | null | undefined) {
    if (typeof rut !== "string") {
      return false;
    }

    if (!/^([1-9]\d{0,2}(\.?\d{3}){2,})-?[\dkK]$/.test(rut)) {
      return false;
    }

    const cleanedRut = RutUtils.clean(rut);

    let t = Number.parseInt(cleanedRut.slice(0, -1), 10);
    let m = 0;
    let s = 1;

    while (t > 0) {
      s = (s + (t % 10) * (9 - (m++ % 6))) % 11;
      t = Math.floor(t / 10);
    }

    const v = s > 0 ? `${s - 1}` : "K";
    return v === cleanedRut.slice(-1);
  }

  static format(
    rut: string | null | undefined,
    options: FormatOptions = { dots: true }
  ) {
    if (!rut) {
      return "";
    }

    const hasInvalidCharacters = /[^0-9kK.\-\s]/i.test(rut);
    const endsWithInvalidChar = /[.\-\s]$/.test(rut);
    const hasInvalidPattern = /-.*[.-]/.test(rut);

    if (hasInvalidCharacters || endsWithInvalidChar || hasInvalidPattern) {
      return rut;
    }

    const cleanedRut = RutUtils.clean(rut);

    let result: string;
    if (options.dots) {
      result = `${cleanedRut.slice(-4, -1)}-${cleanedRut.slice(-1)}`;
      for (let i = 4; i < cleanedRut.length; i += 3) {
        result = `${cleanedRut.slice(-3 - i, -i)}.${result}`;
      }
    } else {
      result = `${cleanedRut.slice(0, -1)}-${cleanedRut.slice(-1)}`;
    }

    return result;
  }

  static getCheckDigit(input: string | null | undefined) {
    const cleanedRut = RutUtils.clean(input);
    const rut = Array.from(cleanedRut, Number);

    if (rut.length === 0 || rut.includes(Number.NaN)) {
      throw new Error(`"${input}" as RUT is invalid`);
    }

    const modulus = 11;
    const initialValue = 0;
    const sumResult = rut
      .reverse()
      .reduce(
        (accumulator, currentValue, index) =>
          accumulator + currentValue * ((index % 6) + 2),
        initialValue
      );

    const checkDigit = modulus - (sumResult % modulus);

    if (checkDigit === 10) {
      return "K";
    }
    if (checkDigit === 11) {
      return "0";
    }
    return checkDigit.toString();
  }
}

export default function rutValidator() {
  return (control: AbstractControl): ValidationErrors | null => {
    const rut = control.value;
    if (!rut) return null;
    const isValid = RutUtils.validate(rut);
    return isValid ? null : { rutInvalid: { value: rut } };
  };
}
